import { ProjectModel } from 'api-hooks/projects';
import { Option } from 'baseui/select';
import { AuthorizationRules } from 'common/constants';
import { ProjectStatusEnum } from 'common/constants/enum';
import { useFormState } from 'components/elements/form';
import Popover, { PopoverMenuType } from 'components/popover';
import useApprovalAction, { isDataDraft } from 'hooks/use-approval-action';
import { useAuthorization } from 'hooks/use-authorization';
import useDrawer from 'hooks/use-drawer';
import { isDataActive } from 'hooks/use-void-action';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';

import AddInvoiceFormPanel from './add-invoice-form-panel';
import RemoveInvoiceFormPanel from './remove-invoice-form-panel';
import useProjectCancelAction from '../hooks/use-project-cancel-action';
import useProjectCompleteAction from '../hooks/use-project-complete-action';

interface Props {
  project: ProjectModel;
  onCancel: () => void;
  cancelLoading?: boolean;
  options?: Option;
}

export default function ActionComponent(props: Props) {
  const { t } = useTranslation();
  const { setIsEditable } = useFormState();
  const drawer = useDrawer();
  const { can } = useAuthorization();
  const { requestApprovalButton } = useApprovalAction(
    'projects',
    props.project,
  );
  const { completeComponent, revertCompleteComponent } =
    useProjectCompleteAction(props.project);

  const status = props.project.status;

  const { cancelAction } = useProjectCancelAction(props.project);

  //add invoice
  const isAllowedAddInvoice = React.useMemo(() => {
    switch (status) {
      case ProjectStatusEnum.Active:
      case ProjectStatusEnum.CancelDraft:
      case ProjectStatusEnum.PendingCancelApproval:
      case ProjectStatusEnum.Canceled:
        return true;
      default:
        return false;
    }
  }, [status]);
  const addInvoicePermission =
    can(AuthorizationRules.ProjectsAddSale) && isAllowedAddInvoice;
  const onAddInvoice = React.useCallback(
    (params?: {
      data?: {
        saleRefId: string;
        date?: Date;
        code?: string;
        description?: string;
        customerId?: string;
      };
    }) => {
      drawer.showCustom({
        render: (onClose) => (
          <AddInvoiceFormPanel
            onClose={onClose}
            project={props.project!}
            {...params}
          />
        ),
      });
    },
    [drawer, props.project],
  );
  const addInvoiceAction: PopoverMenuType = {
    key: 'add-invoices',
    label: t('common:add_invoice'),
    isAllowed: addInvoicePermission,
    onClick() {
      onAddInvoice();
    },
  };

  //remove invoice
  const isAllowedRemoveInvoice = React.useMemo(() => {
    switch (status) {
      case ProjectStatusEnum.Active:
      case ProjectStatusEnum.CancelDraft:
      case ProjectStatusEnum.PendingCancelApproval:
      case ProjectStatusEnum.Canceled:
        return true;
      default:
        return false;
    }
  }, [status]);
  const removeInvoicePermission =
    can(AuthorizationRules.ProjectsRemoveSale) && isAllowedRemoveInvoice;
  const onRemoveInvoice = React.useCallback(() => {
    drawer.showCustom({
      render: (onClose) => (
        <RemoveInvoiceFormPanel
          onClose={onClose}
          project={props.project}
          options={props.options}
        />
      ),
    });
  }, [drawer, props.options, props.project]);
  const removeInvoiceAction: PopoverMenuType = {
    key: 'remove-invoice',
    label: t('common:remove_invoice'),
    isAllowed: removeInvoicePermission,
    onClick() {
      onRemoveInvoice();
    },
  };

  //edit project
  const isActive = isDataActive(status);
  const editPermission = can(AuthorizationRules.ProjectsUpdate);
  const editAction: PopoverMenuType = {
    key: 'editable',
    label: t('common:edit_project'),
    isAllowed: isActive && editPermission,
    onClick() {
      setIsEditable(true);
    },
  };

  const actions = [
    editAction,
    addInvoiceAction,
    removeInvoiceAction,
    cancelAction,
  ].filter((action) => action.isAllowed);

  const isDraft = isDataDraft(status);

  return (
    <div
      style={{
        display: 'flex',
        gap: 16,
      }}
    >
      {isDraft ? (
        <>{requestApprovalButton}</>
      ) : (
        <>
          <Popover actions={actions} />
          {revertCompleteComponent}
          {completeComponent}
        </>
      )}
    </div>
  );
}
